import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { FaLock, FaSyncAlt, FaShieldAlt, FaHeadset } from 'react-icons/fa';
import {
  BillingPeriod,
  PLANS,
  PlanIcon,
  formatPrice,
  getDisplayPrice,
} from '../config/subscriptionPlans.tsx';
import { formatFaunderPurchased, useFaunderStats } from '../hooks/useFaunderStats.ts';
import './css/SubscriptionPage.css';

function HeroGlobe() {
  return (
    <div className="sub-hero-visual" aria-hidden>
      <img
        src="/home/hero-planet.png"
        alt=""
        className="sub-hero-image"
        loading="eager"
        decoding="async"
      />
    </div>
  );
}

function SubscriptionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  const {
    purchasedCount: faunderPurchasedCount,
    purchaseLimit: faunderPurchaseLimit,
    alreadyPurchased: faunderOwned,
    soldOut: faunderSoldOut,
  } = useFaunderStats();

  const handleSelectPlan = (planId: string, yearlyOnly?: boolean) => {
    const period = yearlyOnly ? 'yearly' : billing;
    navigate(`/subscription/checkout?plan=${planId}&period=${period}`);
  };

  const trustItems = [
    { icon: <FaLock />, titleKey: 'subscription.trust.secureTitle', descKey: 'subscription.trust.secureDesc' },
    { icon: <FaSyncAlt />, titleKey: 'subscription.trust.cancelTitle', descKey: 'subscription.trust.cancelDesc' },
    { icon: <FaShieldAlt />, titleKey: 'subscription.trust.guaranteeTitle', descKey: 'subscription.trust.guaranteeDesc' },
    { icon: <FaHeadset />, titleKey: 'subscription.trust.supportTitle', descKey: 'subscription.trust.supportDesc' },
  ];

  return (
    <div className="subscription-page">
      <div className="subscription-container">
        <section className="sub-hero">
          <div className="sub-hero-grid">
            <div className="sub-hero-content">
              <h1 className="sub-hero-title">
                <span className="sub-hero-title-line">{t('subscription.titleLine1')}</span>
                <span className="sub-hero-title-line sub-hero-title-line--accent">{t('subscription.titleLine2')}</span>
              </h1>
              <p className="sub-hero-subtitle">{t('subscription.subtitle')}</p>
            </div>
            <HeroGlobe />
          </div>

          <div className="sub-billing-toggle" role="group" aria-label={t('subscription.periodSelector')}>
            <button
              type="button"
              className={`sub-billing-option ${billing === 'monthly' ? 'sub-billing-option--active' : ''}`}
              onClick={() => setBilling('monthly')}
            >
              {t('subscription.billingMonthly')}
            </button>
            <button
              type="button"
              className={`sub-billing-option ${billing === 'yearly' ? 'sub-billing-option--active' : ''}`}
              onClick={() => setBilling('yearly')}
            >
              {t('subscription.billingYearly')}
              <span className="sub-billing-discount">{t('subscription.billingDiscount')}</span>
            </button>
          </div>
        </section>

        <section className="sub-plans" id="plans">
          <div className="sub-plans-grid">
            {PLANS.map((plan) => {
              const yearlyOnly = plan.yearlyOnlyPrice != null;
              const displayPrice = plan.monthlyPrice != null ? getDisplayPrice(plan.monthlyPrice, billing) : null;
              return (
                <article
                  key={plan.id}
                  className={`sub-plan-card ${plan.popular ? 'sub-plan-card--popular' : ''} ${plan.exclusive ? 'sub-plan-card--exclusive' : ''}`}
                >
                  {plan.popular && (
                    <span className="sub-plan-badge">{t('subscription.popular')}</span>
                  )}
                  {plan.exclusive && (
                    <span className="sub-plan-badge sub-plan-badge--exclusive">{t('subscription.exclusive')}</span>
                  )}

                  <div className="sub-plan-icon-wrap">
                    <PlanIcon type={plan.icon} />
                  </div>

                  <h2 className={`sub-plan-name ${plan.id !== 'lite' ? 'sub-plan-name--accent' : ''}`}>
                    {t(plan.nameKey)}
                  </h2>
                  <p className="sub-plan-description">{t(plan.descriptionKey)}</p>

                  <div className="sub-plan-price">
                    {yearlyOnly ? (
                      <>
                        <span className="sub-plan-price-value">{formatPrice(plan.yearlyOnlyPrice!)} ₽</span>
                        <span className="sub-plan-price-period">{t('subscription.perYearSuffix')}</span>
                      </>
                    ) : (
                      <>
                        <span className="sub-plan-price-value">{formatPrice(displayPrice!)} ₽</span>
                        <span className="sub-plan-price-period">{t('subscription.perMonthSuffix')}</span>
                      </>
                    )}
                  </div>
                  <p className="sub-plan-yearly-note">
                    {yearlyOnly ? t('subscription.faunder.yearlyOnly') : '\u00a0'}
                  </p>

                  <button
                    type="button"
                    className={`sub-plan-button ${plan.popular ? 'sub-plan-button--primary' : ''}`}
                    onClick={() => handleSelectPlan(plan.id, yearlyOnly)}
                    disabled={plan.id === 'faunder' && (faunderOwned || faunderSoldOut)}
                  >
                    {plan.id === 'faunder' && faunderOwned
                      ? t('subscription.faunder.alreadyOwned')
                      : t('subscription.select')}
                  </button>

                  <ul className="sub-plan-features">
                    {plan.features.map((feature) => (
                      <li
                        key={feature.key}
                        className={feature.included ? 'sub-plan-feature--included' : 'sub-plan-feature--excluded'}
                      >
                        <span className="sub-plan-feature-icon" aria-hidden>
                          {feature.included ? '✓' : '✕'}
                        </span>
                        {t(feature.key)}
                      </li>
                    ))}
                  </ul>
                  {plan.id === 'faunder' && (
                    <p className="sub-plan-faunder-count" aria-live="polite">
                      {formatFaunderPurchased(
                        t('subscription.faunder.purchasedCount'),
                        faunderPurchasedCount,
                        faunderPurchaseLimit,
                      )}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        <section className="sub-trust">
          <div className="sub-trust-card">
            <div className="sub-trust-grid">
              {trustItems.map((item) => (
                <article key={item.titleKey} className="sub-trust-item">
                  <span className="sub-trust-icon">{item.icon}</span>
                  <h3 className="sub-trust-title">{t(item.titleKey)}</h3>
                  <p className="sub-trust-desc">{t(item.descKey)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SubscriptionPage;
